import React, { useState, useEffect } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { FaTractor } from 'react-icons/fa';
import { useUser } from '../contexts/UserContext';
import { apiRequest } from '../config/api';
import toast from 'react-hot-toast';
import AccountSelectionModal, { SelectableItem } from './AccountSelectionModal';
import { useTranslation } from 'react-i18next';

interface Farm {
  id: string;
  accountName: string;
  farm: {
    id: string;
    name: string;
    address?: string;
  };
  isActive: boolean;
}

interface CorporateFarmSelectorProps {
  selectedFarm: any;
  onFarmSelect: (farm: any) => void;
}

const CorporateFarmSelector: React.FC<CorporateFarmSelectorProps> = ({
  selectedFarm,
  onFarmSelect
}) => {
  const { t } = useTranslation('common');
  const { user, isCorporateManager, isCorporateMember } = useUser();
  const [showModal, setShowModal] = useState(false);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.corporateAccountId) {
      fetchFarms();
    }
  }, [user?.corporateAccountId]);


  const fetchFarms = async () => {
    if (!user?.corporateAccountId) return;

    try {
      setLoading(true);
      
      // Always fetch all farms first
      const allFarms = await apiRequest<any>(`/api/corporate/${user.corporateAccountId}/individual-accounts`);
      
      if (isCorporateManager || user?.corporateRole === 'MANAGER') {
        // Managers can see all farms
        setFarms(allFarms);
      } else {
        // Members need to fetch their access - check both CORPORATE users and SUB_USERs
        let currentUser = null;
        
        // First, try fetching from CORPORATE users
        try {
          const corporateUsersResponse = await apiRequest<any>(`/api/corporate/${user.corporateAccountId}/users`);
          currentUser = corporateUsersResponse.find((u: any) => u.id === user.id);
        } catch (error) {
          console.log('User not found in corporate users, checking sub-users...');
        }
        
        // If not found, try SUB_USER endpoint
        if (!currentUser) {
          try {
            const subUsersResponse = await apiRequest<any>(`/api/corporate/${user.corporateAccountId}/sub-users`);
            currentUser = subUsersResponse.find((u: any) => u.id === user.id);
          } catch (error) {
            console.error('User not found in sub-users either');
          }
        }
        
        if (currentUser?.corporateUserAccess && currentUser.corporateUserAccess.length > 0) {
          const accessibleFarmIds = currentUser.corporateUserAccess.map((access: any) => access.individualAccountId);
          const accessibleFarms = allFarms.filter((farm: Farm) => accessibleFarmIds.includes(farm.id));
          setFarms(accessibleFarms);
        } else {
          setFarms([]);
        }
      }
    } catch (error: any) {
      console.error('Error fetching farms:', error);
      toast.error(error.message || 'Failed to load farms');
      setFarms([]);
    } finally {
      setLoading(false);
    }
  };

  const convertToSelectableItems = (): SelectableItem[] => {
    const items: SelectableItem[] = [];

    // Sort farms: active first, then by name
    const sortedFarms = [...farms].sort((a, b) => {
      if (a.isActive !== b.isActive) {
        return a.isActive ? -1 : 1;
      }
      return a.farm.name.localeCompare(b.farm.name);
    });

    sortedFarms.forEach(farm => {
      items.push({
        id: farm.id,
        name: farm.farm.name,
        subtitle: farm.accountName,
        badge: farm.isActive ? t('status.active') : t('status.inactive'),
        badgeColor: farm.isActive ? 'green' : 'gray',
        icon: <FaTractor className={`size-4 ${farm.isActive ? 'text-emerald-600' : 'text-slate-400'}`} />,
        metadata: farm,
        isActive: farm.isActive
      });
    });

    return items;
  };

  const handleItemSelect = (item: SelectableItem | null) => {
    if (!item) {
      onFarmSelect(null);
      return;
    }

    if (item.metadata) {
      onFarmSelect(item.metadata);
    }
  };

  const getDisplayText = () => {
    if (!selectedFarm) return t('selection.selectFarm');
    return `🏡 ${selectedFarm.farm?.name || selectedFarm.accountName}`;
  };

  const getSelectedItem = (): SelectableItem | null => {
    if (!selectedFarm) return null;

    const allItems = convertToSelectableItems();
    return allItems.find(item => item.id === selectedFarm.id) || null;
  };

  return (
    <>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {t('selection.farmSelection')}
        </label>
        
        {/* Selected Farm Display - Opens Modal */}
        <button
          onClick={() => setShowModal(true)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-white hover:bg-gray-50 flex items-center justify-between group transition-all"
          disabled={loading}
        >
          <span className={selectedFarm ? 'text-gray-900' : 'text-gray-500'}>
            {loading ? t('selection.loadingFarms') : getDisplayText()}
          </span>
          <FiChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
        </button>

        {/* Info Text */}
        {!loading && farms.length === 0 && (
          <p className="text-xs text-gray-500">
            {isCorporateMember 
              ? t('selection.noFarmsAssignedContactManager')
              : t('selection.noFarmsAvailableCorporate')}
          </p>
        )}
      </div>

      {/* Farm Selection Modal */}
      <AccountSelectionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handleItemSelect}
        items={convertToSelectableItems()}
        selectedItem={getSelectedItem()}
        title={t('selection.selectFarm')}
        searchPlaceholder={t('selection.searchPlaceholderFarm')}
        emptyMessage={isCorporateMember 
          ? t('selection.noFarmsAssignedContactOwner')
          : t('selection.noFarmsAvailable')}
        showClearSelection={true}
        allowGroupExpansion={false}
        showActiveFilter={true}
      />
    </>
  );
};

export default CorporateFarmSelector;

